/**
 * Shared oscillator signal hook (spec §11.6).
 *
 * Single module-scope cache so the top banner and deployment strip never
 * double-fetch. The cached payload is invalidated when the US trading-day
 * trade_date advances (detected on each subscriber mount) so owners who leave
 * the tab open overnight always pick up the fresh reading on next refresh.
 */

import { useEffect, useState } from 'react';
import { getOscillatorSignal } from '../api/client';

// Module-scope state shared across all hook consumers in the session.
let cache = null;               // { trade_date, value, zone, direction_change, previous_value, available }
let inflight = null;            // Promise<Signal|null>
const listeners = new Set();    // Set<(state) => void>

function todayISO() {
  // Use local date boundary — the scheduler persists at 5 PM ET so the
  // user's clock advancing to the next day is a safe invalidation trigger.
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function emit(state) {
  listeners.forEach((fn) => {
    try { fn(state); } catch (_) { /* swallow listener errors */ }
  });
}

async function fetchSignal() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await getOscillatorSignal();
      const next = data && data.available
        ? {
            status: 'ready',
            signal: {
              trade_date: data.trade_date,
              value: data.value,
              zone: data.zone,
              direction_change: data.direction_change,
              previous_value: data.previous_value,
            },
            fetchedAt: Date.now(),
            error: null,
          }
        : { status: 'empty', signal: null, fetchedAt: Date.now(), error: null };
      cache = next;
      emit(next);
      return next;
    } catch (err) {
      const next = { status: 'error', signal: null, fetchedAt: Date.now(), error: err };
      cache = next;
      emit(next);
      return next;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** Force the next `useOscillatorSignal` subscriber to refetch. */
export function invalidateOscillatorSignal() {
  cache = null;
  inflight = null;
  emit({ status: 'loading', signal: null, fetchedAt: 0, error: null });
}

export default function useOscillatorSignal() {
  const [state, setState] = useState(() => {
    if (cache && cache.status === 'ready' && cache.signal?.trade_date === todayISO()) {
      return cache;
    }
    if (cache && cache.status === 'empty' && cache.fetchedAt > Date.now() - 5 * 60 * 1000) {
      return cache;
    }
    return { status: 'loading', signal: null, fetchedAt: 0, error: null };
  });

  useEffect(() => {
    listeners.add(setState);
    const fresh =
      cache &&
      cache.status === 'ready' &&
      cache.signal?.trade_date === todayISO();
    const recentEmpty =
      cache &&
      cache.status === 'empty' &&
      cache.fetchedAt > Date.now() - 5 * 60 * 1000;
    if (!fresh && !recentEmpty) {
      fetchSignal();
    }
    return () => {
      listeners.delete(setState);
    };
  }, []);

  return state;
}
